/**
 * Transition Queue Hook for Proscriptive Workflow v1.0
 * 
 * Manages offline queue for store visit state transitions (start, complete, signoff)
 * Automatically retries transitions when network connectivity returns.
 */

import { useEffect, useState, useCallback } from 'react';
import { set, get, del, keys } from 'idb-keyval';
import { v4 as uuidv4 } from 'uuid';
import { apiFetch } from '@/lib/api';
import { dbg } from '@/lib/dbg';

export type TransitionType = 'STORE_START' | 'STORE_COMPLETE' | 'STORE_SIGNOFF';

export interface QueuedTransition {
  id: string;
  type: TransitionType;
  storeId: number;
  operatorId: number;
  cycleId: number;
  signature?: string; // For STORE_SIGNOFF
  createdAt: number;
  retryCount: number;
}

const TRANSITION_QUEUE_PREFIX = 'transition_queue_';
const MAX_RETRIES = 3;

export function useTransitionQueue() {
  const [pending, setPending] = useState<QueuedTransition[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Load queue from IndexedDB on mount
  useEffect(() => {
    loadQueue();
  }, []);

  const loadQueue = useCallback(async () => {
    try {
      const allKeys = await keys();
      const queueKeys = allKeys.filter(k => 
        typeof k === 'string' && k.startsWith(TRANSITION_QUEUE_PREFIX)
      );
      
      const transitions: QueuedTransition[] = [];
      for (const key of queueKeys) {
        const item = await get(key);
        if (item) {
          transitions.push(item);
        }
      }
      
      // Sort by createdAt to maintain FIFO order
      transitions.sort((a, b) => a.createdAt - b.createdAt);
      
      setPending(transitions);
      dbg.log('[TRANSITION_QUEUE] Loaded queue:', transitions.length, 'items');
    } catch (error) {
      dbg.error('[TRANSITION_QUEUE] Failed to load queue:', error);
    }
  }, []);

  /**
   * Add a transition to the queue
   */
  const enqueue = useCallback(async (
    type: TransitionType,
    storeId: number,
    operatorId: number,
    cycleId: number,
    signature?: string
  ): Promise<string> => {
    try {
      const id = uuidv4();
      
      const entry: QueuedTransition = {
        id,
        type,
        storeId,
        operatorId,
        cycleId,
        signature,
        createdAt: Date.now(),
        retryCount: 0,
      };

      await set(`${TRANSITION_QUEUE_PREFIX}${id}`, entry);
      setPending(prev => [...prev, entry].sort((a, b) => a.createdAt - b.createdAt));
      
      dbg.log('[TRANSITION_QUEUE] Queued transition:', {
        id,
        type,
        storeId,
        operatorId,
        cycleId,
      });

      return id;
    } catch (error) {
      dbg.error('[TRANSITION_QUEUE] Failed to enqueue:', error);
      throw new Error('Failed to queue transition for offline processing');
    }
  }, []);

  /**
   * Process a single transition
   */
  const processTransition = useCallback(async (transition: QueuedTransition): Promise<'success' | 'skipped' | 'failed'> => {
    try {
      let endpoint: string;
      let body: any;

      switch (transition.type) {
        case 'STORE_START':
          endpoint = `/api/mobile/store-start/${transition.storeId}`;
          body = {
            operatorId: transition.operatorId,
            cycleId: transition.cycleId,
          };
          break;
        case 'STORE_COMPLETE':
          endpoint = `/api/mobile/store-complete/${transition.storeId}`;
          body = {
            operatorId: transition.operatorId,
            cycleId: transition.cycleId,
          };
          break;
        case 'STORE_SIGNOFF':
          endpoint = `/api/mobile/store-signoff/${transition.storeId}`;
          body = {
            signature: transition.signature,
            operatorId: transition.operatorId,
            cycleId: transition.cycleId,
          };
          break;
        default:
          throw new Error(`Unknown transition type: ${transition.type}`);
      }

      const response = await apiFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      // Handle 409 conflict (another store active, already signed off, etc.)
      if (response.status === 409) {
        dbg.warn('[TRANSITION_QUEUE] Skipping transition - conflict', {
          id: transition.id,
          type: transition.type,
          storeId: transition.storeId,
        });
        // Remove from queue - conflict means state already applied
        await del(`${TRANSITION_QUEUE_PREFIX}${transition.id}`);
        return 'skipped';
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      // Success - remove from queue
      await del(`${TRANSITION_QUEUE_PREFIX}${transition.id}`);
      dbg.log('[TRANSITION_QUEUE] Transition success:', {
        id: transition.id,
        type: transition.type,
        storeId: transition.storeId,
      });
      return 'success';
    } catch (error) {
      dbg.warn('[TRANSITION_QUEUE] Transition failed:', {
        id: transition.id,
        type: transition.type,
        error: error instanceof Error ? error.message : String(error),
      });
      return 'failed';
    }
  }, []);

  /**
   * Process all pending transitions in the queue (FIFO order)
   */
  const processQueue = useCallback(async () => {
    if (isProcessing || pending.length === 0) {
      return;
    }

    if (!navigator.onLine) {
      dbg.log('[TRANSITION_QUEUE] Cannot process queue: offline');
      return;
    }

    setIsProcessing(true);
    dbg.log('[TRANSITION_QUEUE] Processing queue:', pending.length, 'items');

    const results = {
      success: 0,
      failed: 0,
      skipped: 0,
    };

    // Process in FIFO order
    for (const transition of pending) {
      // Skip items that have exceeded retry limit
      if (transition.retryCount >= MAX_RETRIES) {
        dbg.warn('[TRANSITION_QUEUE] Max retries exceeded:', transition.id);
        results.skipped++;
        continue;
      }

      const result = await processTransition(transition);

      if (result === 'success') {
        results.success++;
      } else if (result === 'skipped') {
        results.skipped++;
      } else {
        // Failed - increment retry count
        const updatedTransition = {
          ...transition,
          retryCount: transition.retryCount + 1,
        };
        
        await set(`${TRANSITION_QUEUE_PREFIX}${transition.id}`, updatedTransition);
        results.failed++;
      }
    }

    dbg.log('[TRANSITION_QUEUE] Queue processing complete:', results);

    // Reload queue to reflect changes
    await loadQueue();
    setIsProcessing(false);
  }, [pending, isProcessing, loadQueue, processTransition]);

  /**
   * Manually remove a transition from the queue
   */
  const removeFromQueue = useCallback(async (id: string) => {
    try {
      await del(`${TRANSITION_QUEUE_PREFIX}${id}`);
      setPending(prev => prev.filter(item => item.id !== id));
      dbg.log('[TRANSITION_QUEUE] Removed from queue:', id);
    } catch (error) {
      dbg.error('[TRANSITION_QUEUE] Failed to remove from queue:', error);
    }
  }, []);

  /**
   * Clear all transitions from the queue
   */
  const clearQueue = useCallback(async () => {
    try {
      const allKeys = await keys();
      const queueKeys = allKeys.filter(k => 
        typeof k === 'string' && k.startsWith(TRANSITION_QUEUE_PREFIX)
      );
      
      for (const key of queueKeys) {
        await del(key);
      }
      
      setPending([]);
      dbg.log('[TRANSITION_QUEUE] Queue cleared');
    } catch (error) {
      dbg.error('[TRANSITION_QUEUE] Failed to clear queue:', error);
    }
  }, []);

  return {
    pending,
    pendingCount: pending.length,
    isProcessing,
    enqueue,
    processQueue,
    removeFromQueue,
    clearQueue,
  };
}

